export const TAG_VALUE_BATCH_SIZE = 25
export const TAG_VALUE_BATCH_CONCURRENCY = 3

interface IncrementalBatchOptions<Item, Result> {
  items: readonly Item[]
  load: (batch: Item[]) => Promise<Result>
  onBatch: (result: Result, batch: Item[]) => void
  onError?: (error: unknown, batch: Item[]) => void
  shouldContinue?: () => boolean
  batchSize?: number
  concurrency?: number
}

/**
 * Loads a snapshot in bounded parallel batches and reports each response as
 * soon as it lands. This gives the table progressive updates without a
 * long-lived streaming response or recurring polling.
 */
export async function loadIncrementalBatches<Item, Result>({
  items,
  load,
  onBatch,
  onError,
  shouldContinue = () => true,
  batchSize = TAG_VALUE_BATCH_SIZE,
  concurrency = TAG_VALUE_BATCH_CONCURRENCY
}: IncrementalBatchOptions<Item, Result>): Promise<void> {
  if (items.length === 0 || !shouldContinue()) return

  const size = Math.max(1, Math.floor(batchSize))
  const batchCount = Math.ceil(items.length / size)
  const workerCount = Math.min(batchCount, Math.max(1, Math.floor(concurrency)))
  let nextStart = 0

  const worker = async (): Promise<void> => {
    while (shouldContinue()) {
      const start = nextStart
      nextStart += size
      if (start >= items.length) return

      const batch = items.slice(start, start + size)
      try {
        const result = await load(batch)
        if (shouldContinue()) onBatch(result, batch)
      } catch (error) {
        if (shouldContinue()) onError?.(error, batch)
      }
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => worker()))
}
